"use client";

import type { ElementType, ReactNode } from "react";
import { forwardRef } from "react";
import { Button, type ButtonProps } from "./button";
import type { IconKind } from "./icon";
import { Icon } from "./icon";
import { HStack } from "./layout/stack";
import { cn } from "./lib/utils";
import { Tooltip } from "./tooltip";

type ActionProps = Omit<ButtonProps, "size"> & {
	icon: IconKind;
	iconClassName?: string;
	disabled?: boolean;
	tooltip?: string;
	active?: boolean;
};

export function ActionGroup({
	children,
	className,
	ele = "div",
}: {
	children: ReactNode;
	className?: string;
	ele?: ElementType;
}): React.ReactElement {
	return (
		<HStack className={cn("items-center gap-min", className)} ele={ele}>
			{children}
		</HStack>
	);
}

export const Action = forwardRef<HTMLButtonElement, ActionProps>(
	(
		{
			className,
			icon,
			iconClassName,
			disabled,
			tooltip,
			onClick,
			active,
			...props
		},
		ref,
	) => {
		const body = (
			<Button
				className={cn(
					"border border-transparent hover:border-card-border-muted",
					"flex-shrink-0 flex-grow-0",
					"text-surface-icon",
					"disabled:cursor-not-allowed disabled:opacity-30",
					active ? "bg-button-bg-ghost" : "",
					className,
				)}
				disabled={disabled}
				intent="ghost"
				onClick={onClick}
				ref={ref}
				size="icon"
				{...props}
			>
				<Icon kind={icon} size="xs" className={iconClassName} />
			</Button>
		);

		if (tooltip) {
			return (
				<Tooltip text={tooltip} side="top">
					{body}
				</Tooltip>
			);
		}

		return body;
	},
);

Action.displayName = "Action";
